import { CollectionResponse } from "../../models/responses/CollectionResponse";

export type FolderNode = {
  id: number;
  name: string;
  parentCollectionId: number | null;
  children: FolderNode[];
  originalCollection: CollectionResponse;
};

export function buildFolderTree(collections: CollectionResponse[]): FolderNode[] {
  const map = new Map<number, FolderNode>();

  collections.forEach(c => {
    map.set(c.Id, {
      id: c.Id,
      name: c.Name,
      parentCollectionId: c.ParentCollectionId ?? null,
      children: [],
      originalCollection: c,
    });
  });

  const roots: FolderNode[] = [];

  map.forEach(node => {
    if (node.parentCollectionId === null) {
      roots.push(node);
    } else {
      const parent = map.get(node.parentCollectionId);
      if (parent) {
        parent.children.push(node);
      } else {
        // Orphan — show as root
        roots.push(node);
      }
    }
  });

  const sortAlpha = (nodes: FolderNode[]) => {
    nodes.sort((a, b) => a.name.localeCompare(b.name));
    nodes.forEach(n => sortAlpha(n.children));
  };
  sortAlpha(roots);

  // Make system collection the visual root; all other root folders become its children
  const systemIdx = roots.findIndex(n => n.originalCollection.IsSystemCollection);
  if (systemIdx === -1) return roots;

  const sysNode = roots[systemIdx];
  const nonSystemRoots = roots.filter((_, i) => i !== systemIdx);
  // nonSystemRoots are already alpha-sorted; sysNode.children should be [] in practice
  sysNode.children = [...nonSystemRoots, ...sysNode.children];

  return [sysNode];
}

export function findNodeById(nodes: FolderNode[], id: number): FolderNode | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    const found = findNodeById(node.children, id);
    if (found) return found;
  }
  return null;
}
